/**
 * TASKS-V2 E2 — agrupamento temporal + algoritmo de foco.
 *
 * Não muda payload. Recebe lista flat de tasks (mesma shape do
 * GET /api/tasks) e devolve buckets + escolha de foco.
 *
 * Buckets em ordem fixa de prioridade temporal:
 *   overdue, today, doing_no_due, tomorrow, this_week, future, no_due, done
 *
 * pickFocusTask consome a mesma ordem com tie-break consistente:
 *   1. priority high > medium > low
 *   2. menor datetime_due (mais cedo primeiro)
 *   3. mais antiga (created_at)
 */

export const GROUP_ORDER = [
  "overdue", "today", "tomorrow", "this_week", "future", "no_due", "done",
];

export const GROUP_LABELS = {
  overdue: "Atrasadas",
  today: "Hoje",
  tomorrow: "Amanhã",
  this_week: "Esta semana",
  future: "Próximas",
  no_due: "Sem prazo",
  done: "Concluídas",
};

// Dot colors (Tailwind classnames + hex pra subline).
export const GROUP_COLORS = {
  overdue: { dot: "bg-red-500", text: "text-red-300", count: "text-red-400" },
  today: { dot: "bg-cyan-500", text: "text-cyan-300", count: "text-cyan-400" },
  tomorrow: { dot: "bg-blue-400", text: "text-blue-300", count: "text-blue-400" },
  this_week: { dot: "bg-slate-300", text: "text-slate-300", count: "text-slate-400" },
  future: { dot: "bg-slate-500", text: "text-slate-400", count: "text-slate-500" },
  no_due: { dot: "bg-slate-600", text: "text-slate-500", count: "text-slate-600" },
  done: { dot: "bg-emerald-500", text: "text-emerald-300", count: "text-emerald-400" },
};

function _startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function _parseDue(task) {
  const raw = task.datetime_due || task.due_date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function _isDone(task) {
  return task.status === "done" || task.is_completed === true;
}

/**
 * Classifica 1 task no bucket correspondente.
 * Retorna string do GROUP_ORDER ou null se task inválida.
 */
export function classifyTask(task, now = new Date()) {
  if (!task) return null;
  if (_isDone(task)) return "done";

  const due = _parseDue(task);
  if (due === null) {
    // Sem prazo. Se está em andamento, vai pra "today" via doing_no_due
    // semantics — i.e. mostra como prioritário. Mas a especificação
    // pede grupo "Sem prazo" — vamos manter aqui e o pickFocusTask
    // pega doing sem prazo como bucket 3 separadamente.
    return "no_due";
  }

  const today0 = _startOfDay(now);
  const tomorrow0 = new Date(today0);
  tomorrow0.setDate(tomorrow0.getDate() + 1);
  const dayAfterTomorrow0 = new Date(tomorrow0);
  dayAfterTomorrow0.setDate(dayAfterTomorrow0.getDate() + 1);
  const weekEnd0 = new Date(today0);
  weekEnd0.setDate(weekEnd0.getDate() + 7);

  if (due.getTime() < now.getTime()) {
    return "overdue";
  }
  if (due >= today0 && due < tomorrow0) {
    return "today";
  }
  if (due >= tomorrow0 && due < dayAfterTomorrow0) {
    return "tomorrow";
  }
  if (due >= dayAfterTomorrow0 && due < weekEnd0) {
    return "this_week";
  }
  return "future";
}

/**
 * Agrupa lista flat de tasks em buckets temporais.
 * Retorna { overdue: [...], today: [...], ..., done: [...] }.
 * Cada bucket é ordenado por (priority desc, datetime_due asc, created_at asc).
 */
export function groupTasks(tasks, now = new Date()) {
  const out = {};
  for (const g of GROUP_ORDER) out[g] = [];
  for (const t of tasks || []) {
    const g = classifyTask(t, now);
    if (g) out[g].push(t);
  }
  for (const g of GROUP_ORDER) {
    out[g].sort(_compareTasks);
  }
  return out;
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };

function _compareTasks(a, b) {
  const pa = PRIORITY_RANK[a.priority ?? "medium"] ?? 1;
  const pb = PRIORITY_RANK[b.priority ?? "medium"] ?? 1;
  if (pa !== pb) return pa - pb;

  const da = _parseDue(a)?.getTime() ?? Infinity;
  const db = _parseDue(b)?.getTime() ?? Infinity;
  if (da !== db) return da - db;

  const ca = a.created_at ? new Date(a.created_at).getTime() : 0;
  const cb = b.created_at ? new Date(b.created_at).getTime() : 0;
  return ca - cb;
}

/**
 * Algoritmo de foco — pega 1 task pra renderizar no Hero.
 *
 * Ordem de prioridade dos buckets pro foco:
 *   1. overdue
 *   2. today (vence hoje, não atrasada)
 *   3. doing (em andamento, com ou sem prazo)
 *   4. tomorrow
 *   5. this_week
 *   6. future
 *   7. no_due
 *
 * Dentro do bucket, tie-break por (priority high>medium>low,
 * datetime_due asc, created_at asc).
 *
 * Retorna 1 task ou null se nada elegível.
 *
 * skipIds: array de IDs já pulados nesta sessão (botão "pular" no Hero).
 */
export function pickFocusTask(tasks, now = new Date(), skipIds = []) {
  const skipSet = new Set(skipIds);
  const eligible = (tasks || []).filter(
    (t) => !_isDone(t) && !skipSet.has(t.id),
  );
  if (eligible.length === 0) return null;

  const groups = groupTasks(eligible, now);

  // Bucket "doing" não está no groupTasks padrão — extraímos aqui
  // sobre o que sobrou após classifyTask (intersecciona com qualquer
  // grupo onde a task caiu).
  const doing = eligible
    .filter((t) => t.status === "doing")
    .sort(_compareTasks);

  const focusOrder = [
    groups.overdue,
    groups.today,
    doing,
    groups.tomorrow,
    groups.this_week,
    groups.future,
    groups.no_due,
  ];

  for (const bucket of focusOrder) {
    if (bucket && bucket.length > 0) return bucket[0];
  }
  return null;
}
